/**
 * Auto-Intel Generation Utility
 * Automatically generates Pattern of Life (PoL) intelligence notes from analysis data
 * 
 * Generates notes for:
 * - Significant dwell locations (stops > threshold duration)
 * - Co-traveler encounters (device associations)
 * - Repeated visit patterns (sites visited multiple times)
 */

import pathfinderApi, { CoTravelerResult, EntityNote } from '../services/pathfinderApi';
import apiService from '../services/api';

// Cache for reverse geocoding results to avoid duplicate API calls
const geocodeCache = new Map<string, { name: string; city?: string; country?: string }>();

/**
 * Reverse geocode a location to get real place name
 * Uses caching to avoid redundant API calls
 */
async function reverseGeocode(lat: number, lng: number): Promise<{ name: string; city?: string; country?: string } | null> {
  // Create cache key with ~100m precision
  const cacheKey = `${lat.toFixed(4)},${lng.toFixed(4)}`;
  
  // Check cache first
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)!;
  }
  
  try {
    // Call the enrichment API which includes reverse geocoding
    const result = await apiService.enrichLocation(lat, lng);
    
    if (result.success && result.discovered_place_name) {
      const geocoded = {
        name: result.discovered_place_name,
        city: result.discovered_city,
        country: result.discovered_country,
      };
      geocodeCache.set(cacheKey, geocoded);
      return geocoded;
    }
  } catch (error) {
    console.log('[Auto-Intel] Reverse geocoding failed, using generic name:', error);
  }
  
  return null;
}

// Types for the auto-intel system
export interface DwellForIntel {
  id: string | number;
  entity_id: string;
  device_type?: string;
  latitude: number;
  longitude: number;
  start_time: string;
  end_time: string;
  duration_seconds: number;
  site_name?: string;
  place_name?: string;
  place_type?: string;
  address?: string;
  visit_count?: number;
}

export interface RepeatedVisitSite {
  siteId: string;
  siteName: string;
  latitude: number;
  longitude: number;
  visitCount: number;
  totalDuration: number;
  visitTimes: string[];
  placeName?: string;
  placeType?: string;
  entityId: string;
}

export interface WatchlistDevice {
  id: number;
  registration_id: string;
  label?: string;
}

export interface AutoIntelConfig {
  minDwellDurationMinutes: number;  // Minimum dwell duration to report (default 30)
  minCoTravelerEncounters: number;  // Minimum encounters to report (default 3)
  minRepeatedVisits: number;        // Minimum visits to consider a pattern (default 2)
  skipDuplicateCheck?: boolean;     // For testing - skip duplicate checking
}

const DEFAULT_CONFIG: AutoIntelConfig = {
  minDwellDurationMinutes: 30,
  minCoTravelerEncounters: 3,
  minRepeatedVisits: 2,
};

/**
 * Generate a unique signature for a note to detect duplicates
 * Format: POL-{type}-{deviceId}-{locationKey}-{dateKey}
 */
function generateNoteSignature(
  type: 'DWELL' | 'COLOC' | 'REPEAT',
  deviceId: string,
  lat: number,
  lng: number,
  dateKey: string
): string {
  // Round coordinates to ~100m precision to catch similar locations
  const latKey = lat.toFixed(3);
  const lngKey = lng.toFixed(3);
  return `POL-${type}-${deviceId.substring(0, 8)}-${latKey},${lngKey}-${dateKey}`;
}

/**
 * Check if a note with this signature already exists
 */
async function noteExists(
  watchlistId: number,
  signature: string,
  existingNotes?: EntityNote[]
): Promise<boolean> {
  try {
    // Use provided notes cache or fetch fresh
    const notes = existingNotes || await pathfinderApi.getNotes({ watchlist_id: watchlistId });
    return notes.some(note => note.content.includes(signature));
  } catch (error) {
    console.error('Error checking for existing notes:', error);
    return false; // Allow creation if check fails
  }
}

/**
 * Format duration in a human-readable way
 */
function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/**
 * Format a date for display
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

/**
 * Generate a dwell location intel note
 */
function generateDwellNote(dwell: DwellForIntel, signature: string): string {
  const locationName = dwell.place_name || dwell.site_name || 'Unknown Location';
  const locationType = dwell.place_type || 'Location';
  const duration = formatDuration(dwell.duration_seconds);
  const startTime = formatDate(dwell.start_time);
  const endTime = formatDate(dwell.end_time);
  
  return `[POL INTEL] Dwell Location Detected
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**Significant Stop: ${locationName}**

Date: ${new Date(dwell.start_time).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
Category: PoL
Duration: ${duration}

Location: ${dwell.latitude.toFixed(6)}, ${dwell.longitude.toFixed(6)}${locationName !== 'Unknown Location' ? ` (${locationName})` : ''}
Type: ${locationType}

Summary:
Target device observed stationary at this location for ${duration}.
- Start Time: ${startTime}
- End Time: ${endTime}
- Entity ID: ${dwell.entity_id}
${dwell.device_type ? `- Device Type: ${dwell.device_type}` : ''}
${dwell.visit_count && dwell.visit_count > 1 ? `- Visit #${dwell.visit_count} to this site` : ''}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Signature: ${signature}
Auto-generated via Pattern of Life Analysis on ${new Date().toISOString()}`;
}

/**
 * Generate a co-traveler intel note
 */
function generateCoTravelerNote(
  result: CoTravelerResult,
  primaryDeviceLabel: string,
  signature: string
): string {
  const encounterCount = result.totalEncounters;
  const totalDuration = formatDuration(result.totalDuration * 60); // Duration is in minutes
  const confidence = (result.confidenceScore * 100).toFixed(1);
  
  // Get top locations
  const topLocations = result.frequentLocations
    .slice(0, 3)
    .map((loc, i) => `  ${i + 1}. ${loc.name || `${loc.location[0].toFixed(4)}, ${loc.location[1].toFixed(4)}`} (${loc.encounters} encounters)`)
    .join('\n');
  
  // Get first encounter location for the signature
  const firstEncounter = result.encounters[0];
  const locationStr = firstEncounter 
    ? `${firstEncounter.location[0].toFixed(6)}, ${firstEncounter.location[1].toFixed(6)}`
    : 'Multiple locations';

  return `[POL INTEL] Co-Traveler Association Detected
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**Co-Location: ${result.coTravelerDevice}**

Date Range: ${formatDate(result.encounters[0]?.timestamp.toString() || '')} - ${formatDate(result.encounters[result.encounters.length - 1]?.timestamp.toString() || '')}
Category: PoL
Confidence: ${confidence}%

Location: ${locationStr}

Summary:
Target device (${primaryDeviceLabel}) detected in proximity to ${result.coTravelerDevice}.
- Total Encounters: ${encounterCount}
- Total Time Together: ${totalDuration}
- Average Proximity: ${result.averageProximity.toFixed(0)}m
- Temporal Overlap: ${(result.temporalOverlap * 100).toFixed(1)}%
- Spatial Overlap: ${(result.spatialOverlap * 100).toFixed(1)}%

Frequent Meeting Locations:
${topLocations || '  No frequent locations identified'}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Signature: ${signature}
Auto-generated via Pattern of Life Analysis on ${new Date().toISOString()}`;
}

/**
 * Generate a repeated visit pattern intel note
 */
function generateRepeatedVisitNote(site: RepeatedVisitSite, signature: string): string {
  const avgDuration = formatDuration(site.totalDuration / site.visitCount);
  const totalDuration = formatDuration(site.totalDuration);
  
  // Format visit times (show up to 5)
  const visitsList = site.visitTimes
    .slice(0, 5)
    .map((time, i) => `  ${i + 1}. ${formatDate(time)}`)
    .join('\n');
  const moreVisits = site.visitTimes.length > 5 ? `\n  ... and ${site.visitTimes.length - 5} more visits` : '';

  // Use real place name if available, otherwise use generic site name
  const displayName = site.placeName || site.siteName;
  
  // Only show coordinates location line if we don't have a place name
  const locationLine = site.placeName 
    ? `Location: ${displayName}` 
    : `Location: ${site.latitude.toFixed(6)}, ${site.longitude.toFixed(6)}`;

  return `[POL INTEL] Repeated Visit Pattern Detected
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

**${displayName}**

Date: Multiple visits detected
Category: PoL
Visit Count: ${site.visitCount} visits

${locationLine}
Coordinates: ${site.latitude.toFixed(6)}, ${site.longitude.toFixed(6)}
${site.placeType ? `Type: ${site.placeType}` : ''}

Summary:
Target device has visited this location ${site.visitCount} times, indicating a pattern of life.
- Total Time at Location: ${totalDuration}
- Average Visit Duration: ${avgDuration}
- Entity ID: ${site.entityId}

Visit History:
${visitsList}${moreVisits}

━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
Signature: ${signature}
Auto-generated via Pattern of Life Analysis on ${new Date().toISOString()}`;
}

/**
 * Find watchlist entry for a device
 * Handles common prefixes like "GN-", "Student_", etc.
 */
function findWatchlistEntry(
  deviceId: string,
  watchlist: WatchlistDevice[]
): WatchlistDevice | undefined {
  // Strip common prefixes and normalize
  const stripPrefixes = (id: string): string => {
    return id
      .replace(/^(GN-|Student_|Device_|SRP-|RP-)/i, '') // Strip common prefixes
      .toLowerCase()
      .replace(/-/g, '')
      .trim();
  };
  
  const normalizedId = stripPrefixes(deviceId);
  
  const match = watchlist.find(device => {
    const normalizedRegId = stripPrefixes(device.registration_id);
    const normalizedLabel = stripPrefixes(device.label || '');
    
    // Check exact matches first
    if (normalizedRegId === normalizedId) {
      console.log(`[Auto-Intel] MATCH FOUND (exact reg): "${normalizedId}" === "${normalizedRegId}" -> ${device.label || device.registration_id}`);
      return true;
    }
    if (normalizedLabel && normalizedLabel === normalizedId) {
      console.log(`[Auto-Intel] MATCH FOUND (exact label): "${normalizedId}" === "${normalizedLabel}" -> ${device.label}`);
      return true;
    }
    
    // Check substring matches (only if both strings have meaningful length)
    if (normalizedId.length >= 8 && normalizedRegId.length >= 8) {
      if (normalizedRegId.includes(normalizedId) || normalizedId.includes(normalizedRegId)) {
        console.log(`[Auto-Intel] MATCH FOUND (partial reg): "${normalizedId}" <-> "${normalizedRegId}" -> ${device.label || device.registration_id}`);
        return true;
      }
    }
    if (normalizedLabel && normalizedLabel.length >= 4 && normalizedId.length >= 4) {
      if (normalizedLabel.includes(normalizedId) || normalizedId.includes(normalizedLabel)) {
        console.log(`[Auto-Intel] MATCH FOUND (partial label): "${normalizedId}" <-> "${normalizedLabel}" -> ${device.label}`);
        return true;
      }
    }
    
    return false;
  });
  
  if (!match) {
    // Only log once per unique ID to avoid spam
    console.log(`[Auto-Intel] NO MATCH for "${deviceId}" -> "${normalizedId}"`);
  }
  
  return match;
}

/**
 * Main function to generate auto-intel from loaded data
 * Returns count of new intel notes created
 */
export async function generateAutoIntel(
  dwells: DwellForIntel[],
  coTravelerResults: CoTravelerResult[],
  repeatedVisitSites: RepeatedVisitSite[],
  config: Partial<AutoIntelConfig> = {}
): Promise<{ created: number; skipped: number; errors: number }> {
  const cfg = { ...DEFAULT_CONFIG, ...config };
  const stats = { created: 0, skipped: 0, errors: 0 };
  
  try {
    // Get watchlist to find saved devices
    const watchlist = await pathfinderApi.getWatchlist();
    if (!watchlist || watchlist.length === 0) {
      console.log('[Auto-Intel] No devices in watchlist, skipping auto-intel generation');
      return stats;
    }
    
    console.log(`[Auto-Intel] Processing ${dwells.length} dwells, ${coTravelerResults.length} co-travelers, ${repeatedVisitSites.length} repeat sites`);
    console.log(`[Auto-Intel] Watchlist devices:`, JSON.stringify(watchlist.map(d => ({ reg: d.registration_id, label: d.label }))));
    
    // Create a map of existing notes per watchlist device for efficient duplicate checking
    const existingNotesMap = new Map<number, EntityNote[]>();
    for (const device of watchlist) {
      try {
        const notes = await pathfinderApi.getNotes({ watchlist_id: device.id });
        existingNotesMap.set(device.id, notes);
      } catch {
        existingNotesMap.set(device.id, []);
      }
    }
    
    // Process dwells
    const significantDwells = dwells.filter(
      d => d.duration_seconds >= cfg.minDwellDurationMinutes * 60
    );
    
    console.log(`[Auto-Intel] ${significantDwells.length} dwells meet duration threshold (${cfg.minDwellDurationMinutes}+ min)`);
    if (significantDwells.length > 0) {
      console.log('[Auto-Intel] Sample dwell entity_ids:', JSON.stringify(significantDwells.slice(0, 5).map(d => d.entity_id)));
    }
    
    for (const dwell of significantDwells) {
      try {
        const watchlistEntry = await findWatchlistEntry(dwell.entity_id, watchlist);
        if (!watchlistEntry) {
          console.log(`[Auto-Intel] No watchlist match for entity_id: "${dwell.entity_id}"`);
          stats.skipped++;
          continue; // Device not in watchlist
        }
        
        const dateKey = new Date(dwell.start_time).toISOString().split('T')[0];
        const signature = generateNoteSignature(
          'DWELL',
          watchlistEntry.registration_id,
          dwell.latitude,
          dwell.longitude,
          dateKey
        );
        
        // Check for duplicate
        if (!cfg.skipDuplicateCheck) {
          const exists = await noteExists(
            watchlistEntry.id,
            signature,
            existingNotesMap.get(watchlistEntry.id)
          );
          if (exists) {
            if (stats.skipped < 3) {
              console.log(`[Auto-Intel] Skipping duplicate dwell: ${signature}`);
            }
            stats.skipped++;
            continue;
          }
        }
        
        // Try to get real place name via reverse geocoding
        const geocoded = await reverseGeocode(dwell.latitude, dwell.longitude);
        if (geocoded) {
          dwell.place_name = geocoded.name;
          console.log(`[Auto-Intel] Geocoded dwell location: ${geocoded.name}`);
        }
        
        // Create the note
        const noteContent = generateDwellNote(dwell, signature);
        console.log(`[Auto-Intel] Creating dwell note for ${watchlistEntry.label || watchlistEntry.registration_id}:`, signature);
        await pathfinderApi.createNote(noteContent, watchlistEntry.id);
        console.log(`[Auto-Intel] SUCCESS: Created dwell note: ${signature}`);
        stats.created++;
        
        // Update cache (we just need enough fields for duplicate checking)
        const cached = existingNotesMap.get(watchlistEntry.id) || [];
        cached.push({ id: Date.now(), content: noteContent, created_at: new Date().toISOString(), created_by: 'auto-intel' } as EntityNote);
        existingNotesMap.set(watchlistEntry.id, cached);
        
      } catch (error) {
        console.error('Error creating dwell intel:', error);
        stats.errors++;
      }
    }
    
    // Process co-traveler results
    const significantCoTravelers = coTravelerResults.filter(
      r => r.totalEncounters >= cfg.minCoTravelerEncounters
    );
    
    console.log(`[Auto-Intel] ${significantCoTravelers.length} co-travelers meet threshold (${cfg.minCoTravelerEncounters}+ encounters)`);
    if (significantCoTravelers.length > 0) {
      console.log('[Auto-Intel] Sample co-traveler primaryDevices:', JSON.stringify(significantCoTravelers.slice(0, 5).map(r => r.primaryDevice)));
    }
    
    for (const result of significantCoTravelers) {
      try {
        const watchlistEntry = await findWatchlistEntry(result.primaryDevice, watchlist);
        if (!watchlistEntry) {
          console.log(`[Auto-Intel] No watchlist match for co-traveler primaryDevice: "${result.primaryDevice}"`);
          stats.skipped++;
          continue;
        }
        
        // Use first encounter date and co-traveler ID for signature
        const firstEncounter = result.encounters[0];
        const dateKey = firstEncounter 
          ? new Date(firstEncounter.timestamp).toISOString().split('T')[0]
          : new Date().toISOString().split('T')[0];
        const lat = firstEncounter?.location[0] || 0;
        const lng = firstEncounter?.location[1] || 0;
        
        const signature = generateNoteSignature(
          'COLOC',
          `${watchlistEntry.registration_id}-${result.coTravelerDevice.substring(0, 8)}`,
          lat,
          lng,
          dateKey
        );
        
        if (!cfg.skipDuplicateCheck) {
          const exists = await noteExists(
            watchlistEntry.id,
            signature,
            existingNotesMap.get(watchlistEntry.id)
          );
          if (exists) {
            stats.skipped++;
            continue;
          }
        }
        
        const deviceLabel = watchlistEntry.label || watchlistEntry.registration_id;
        const noteContent = generateCoTravelerNote(result, deviceLabel, signature);
        await pathfinderApi.createNote(noteContent, watchlistEntry.id);
        stats.created++;
        
        const cached = existingNotesMap.get(watchlistEntry.id) || [];
        cached.push({ id: Date.now(), content: noteContent, created_at: new Date().toISOString(), created_by: 'auto-intel' } as EntityNote);
        existingNotesMap.set(watchlistEntry.id, cached);
        
      } catch (error) {
        console.error('Error creating co-traveler intel:', error);
        stats.errors++;
      }
    }
    
    // Process repeated visit sites
    const significantSites = repeatedVisitSites.filter(
      s => s.visitCount >= cfg.minRepeatedVisits
    );
    
    console.log(`[Auto-Intel] ${significantSites.length} repeated visit sites meet threshold (${cfg.minRepeatedVisits}+ visits)`);
    if (significantSites.length > 0) {
      console.log('[Auto-Intel] Sample repeated visit entityIds:', JSON.stringify(significantSites.slice(0, 5).map(s => s.entityId)));
    }
    
    for (const site of significantSites) {
      try {
        const watchlistEntry = await findWatchlistEntry(site.entityId, watchlist);
        if (!watchlistEntry) {
          console.log(`[Auto-Intel] No watchlist match for repeated visit entityId: "${site.entityId}"`);
          stats.skipped++;
          continue;
        }
        
        // Use site location and a hash of visit count for uniqueness
        const signature = generateNoteSignature(
          'REPEAT',
          watchlistEntry.registration_id,
          site.latitude,
          site.longitude,
          `v${site.visitCount}`
        );
        
        if (!cfg.skipDuplicateCheck) {
          const exists = await noteExists(
            watchlistEntry.id,
            signature,
            existingNotesMap.get(watchlistEntry.id)
          );
          if (exists) {
            if (stats.skipped < 3) {
              console.log(`[Auto-Intel] Skipping duplicate repeated visit: ${signature}`);
            }
            stats.skipped++;
            continue;
          }
        }
        
        // Try to get real place name via reverse geocoding
        const geocoded = await reverseGeocode(site.latitude, site.longitude);
        if (geocoded) {
          // Replace generic "Site A" name with real place name
          site.siteName = geocoded.name;
          site.placeName = geocoded.name;
          console.log(`[Auto-Intel] Geocoded repeated visit: ${site.siteName} -> ${geocoded.name}`);
        }
        
        const noteContent = generateRepeatedVisitNote(site, signature);
        console.log(`[Auto-Intel] Creating repeated visit note for ${watchlistEntry.label || watchlistEntry.registration_id}:`, signature);
        await pathfinderApi.createNote(noteContent, watchlistEntry.id);
        console.log(`[Auto-Intel] SUCCESS: Created repeated visit note: ${signature}`);
        stats.created++;
        
        const cached = existingNotesMap.get(watchlistEntry.id) || [];
        cached.push({ id: Date.now(), content: noteContent, created_at: new Date().toISOString(), created_by: 'auto-intel' } as EntityNote);
        existingNotesMap.set(watchlistEntry.id, cached);
        
      } catch (error) {
        console.error('Error creating repeated visit intel:', error);
        stats.errors++;
      }
    }
    
    // Log final results
    console.log(`[Auto-Intel] COMPLETE: created=${stats.created}, skipped=${stats.skipped}, errors=${stats.errors}`);
    
    // Trigger refresh of intel packet if any notes were created
    if (stats.created > 0) {
      window.dispatchEvent(new CustomEvent('refresh-intel-packet'));
    }
    
    return stats;
    
  } catch (error) {
    console.error('[Auto-Intel] Error in auto-intel generation:', error);
    return stats;
  }
}

/**
 * Extract repeated visit sites from dwells data
 * Groups dwells by location proximity to identify patterns
 */
export function extractRepeatedVisitSites(
  dwells: DwellForIntel[],
  _proximityThresholdMeters: number = 100
): RepeatedVisitSite[] {
  const sites = new Map<string, RepeatedVisitSite>();
  
  // Simple clustering by rounding coordinates
  for (const dwell of dwells) {
    // Create location key based on ~100m grid
    const latKey = Math.round(dwell.latitude * 1000) / 1000; // ~111m precision
    const lngKey = Math.round(dwell.longitude * 1000) / 1000;
    const siteKey = `${dwell.entity_id}-${latKey},${lngKey}`;
    
    if (sites.has(siteKey)) {
      const site = sites.get(siteKey)!;
      site.visitCount++;
      site.totalDuration += dwell.duration_seconds;
      site.visitTimes.push(dwell.start_time);
      // Update name if we have a better one
      if (dwell.place_name && !site.placeName) {
        site.placeName = dwell.place_name;
        site.siteName = dwell.place_name;
      }
      if (dwell.place_type && !site.placeType) {
        site.placeType = dwell.place_type;
      }
    } else {
      sites.set(siteKey, {
        siteId: siteKey,
        siteName: dwell.place_name || dwell.site_name || `Site at ${latKey}, ${lngKey}`,
        latitude: dwell.latitude,
        longitude: dwell.longitude,
        visitCount: 1,
        totalDuration: dwell.duration_seconds,
        visitTimes: [dwell.start_time],
        placeName: dwell.place_name,
        placeType: dwell.place_type,
        entityId: dwell.entity_id,
      });
    }
  }
  
  // Return sites with multiple visits, sorted by visit count
  return Array.from(sites.values())
    .filter(site => site.visitCount >= 2)
    .sort((a, b) => b.visitCount - a.visitCount);
}

/**
 * Convert various dwell formats to the standard format for intel generation
 * Handles multiple formats:
 * - Backend aggregated dwells: center_latitude, center_longitude, students[], total_duration
 * - Frontend detected dwells: latitude, longitude, entity_id, duration_seconds
 */
export function normalizeDwellData(dwells: any[]): DwellForIntel[] {
  const normalized: DwellForIntel[] = [];
  
  for (let index = 0; index < dwells.length; index++) {
    const dwell = dwells[index];
    
    // Get coordinates (support multiple field names)
    const latitude = dwell.latitude || dwell.center_latitude || (dwell.coordinates?.[0]);
    const longitude = dwell.longitude || dwell.center_longitude || (dwell.coordinates?.[1]);
    
    // Get duration (support multiple field names)
    const duration_seconds = dwell.duration_seconds || dwell.durationSeconds || 
      dwell.total_duration || dwell.average_duration || 0;
    
    // Skip if missing required data
    if (!latitude || !longitude || duration_seconds <= 0) {
      continue;
    }
    
    // Get entity IDs - handle both single entity and array of students
    let entityIds: string[] = [];
    
    if (dwell.entity_id) {
      entityIds = [dwell.entity_id];
    } else if (dwell.studentId) {
      entityIds = [dwell.studentId];
    } else if (dwell.device_id) {
      entityIds = [dwell.device_id];
    } else if (dwell.students && Array.isArray(dwell.students) && dwell.students.length > 0) {
      entityIds = dwell.students;
    }
    
    // If no entity found, skip
    if (entityIds.length === 0) {
      console.log('[Auto-Intel] Dwell missing entity ID:', dwell);
      continue;
    }
    
    // Create a dwell entry for each entity (for aggregated dwells with multiple students)
    for (const entity_id of entityIds) {
      normalized.push({
        id: dwell.id || `dwell-${index}`,
        entity_id,
        device_type: dwell.device_type || dwell.deviceType,
        latitude,
        longitude,
        start_time: dwell.start_time || dwell.startTime || new Date().toISOString(),
        end_time: dwell.end_time || dwell.endTime || new Date().toISOString(),
        duration_seconds,
        site_name: dwell.site_name || dwell.siteName,
        place_name: dwell.place_name || dwell.placeName || dwell.metadata?.name,
        place_type: dwell.place_type || dwell.placeType || dwell.metadata?.type,
        address: dwell.address || dwell.metadata?.address,
        visit_count: dwell.total_visits || dwell.visitCount || 1,
      });
    }
  }
  
  console.log(`[Auto-Intel] Normalized ${dwells.length} dwells to ${normalized.length} entries`);
  return normalized;
}

const autoIntelUtils = {
  generateAutoIntel,
  extractRepeatedVisitSites,
  normalizeDwellData,
};

export default autoIntelUtils;

